import { useState, useMemo } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import {
  Search, Clock, User, Plus, Save,
  Heart, Droplets, Activity, Thermometer,
  FileText, ChevronRight,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

/* ═══ MOCK DATA ═══ */
const PATIENTS = [
  { id: 1, name: 'Verdi Anna', age: 81, setting: 'Media', diagnosis: 'Frattura femore dx', lastUpdate: '10:30' },
  { id: 2, name: 'Ricci Marco', age: 78, setting: 'Media', diagnosis: 'Ictus cerebrale ischemico', lastUpdate: '09:15' },
  { id: 3, name: 'Ferrara Elena', age: 73, setting: 'Alta', diagnosis: 'BPCO riacutizzata', lastUpdate: 'Ieri' },
  { id: 4, name: 'Marchetti Teresa', age: 66, setting: 'Bassa', diagnosis: 'Diabete tipo 2 scompensato', lastUpdate: '11:02' },
  { id: 5, name: 'Esposito Luca', age: 80, setting: 'Alta', diagnosis: 'Scompenso cardiaco cronico', lastUpdate: 'Ieri' },
];

const NOTES = {
  1: [
    { id: 1, date: '13/04/2026', time: '09:30', author: 'Inf. Marco Rossi', role: 'Infermiere', text: 'Effettuata medicazione lesione da decubito sacrale (stadio II). Margini netti, no segni di infezione. Sostituita medicazione avanzata con schiuma di poliuretano. Paziente collaborante.' },
    { id: 2, date: '12/04/2026', time: '15:00', author: 'Ft. Elena Ferrara', role: 'Fisioterapista', text: 'Seduta di riabilitazione n.8. Esercizi di mobilizzazione passiva arto inferiore dx. Paziente riesce a mantenere la posizione seduta per 20 minuti. Progressi nella deambulazione assistita con girello.' },
    { id: 3, date: '11/04/2026', time: '10:00', author: 'Dott. Carla Bianchi', role: 'Medico UVMD', text: 'Visita di controllo programmata. Ferita chirurgica in buona evoluzione. Confermata terapia anticoagulante (Enoxaparina 4000 UI/die). Prossimo controllo ematochimico tra 7 giorni.' },
    { id: 4, date: '10/04/2026', time: '08:45', author: 'Inf. Marco Rossi', role: 'Infermiere', text: 'Rilevazione parametri: PA 130/80, FC 74, SpO2 96%, T 36.4. Somministrata terapia orale come da schema. Paziente riferisce lieve dolore (NRS 3/10) alla mobilizzazione.' },
  ],
  2: [
    { id: 1, date: '13/04/2026', time: '08:00', author: 'Inf. Laura Conti', role: 'Infermiere', text: 'Somministrata terapia orale. Paziente vigile e orientato. Leggera disartria residua. Alimentazione per os regolare.' },
    { id: 2, date: '12/04/2026', time: '14:30', author: 'Ft. Giovanni Mazza', role: 'Fisioterapista', text: 'Riabilitazione neuromotoria. Esercizi per recupero motilita fine mano sinistra. Progressi lenti ma costanti.' },
  ],
};

function getNotesFor(patientId) {
  return NOTES[patientId] || [
    { id: 1, date: '12/04/2026', time: '09:00', author: 'Inf. ADI', role: 'Infermiere', text: 'Accesso domiciliare regolare. Parametri nella norma. Nessuna criticita rilevata.' },
  ];
}

const VITALS = {
  1: { spo2: 96, pa: '130/80', fc: 74, glyc: 95, spo2Status: 'ok', paStatus: 'ok', fcStatus: 'ok', glycStatus: 'ok' },
  3: { spo2: 88, pa: '145/90', fc: 92, glyc: 110, spo2Status: 'critical', paStatus: 'warning', fcStatus: 'warning', glycStatus: 'ok' },
  5: { spo2: 93, pa: '150/95', fc: 88, glyc: 180, spo2Status: 'warning', paStatus: 'critical', fcStatus: 'warning', glycStatus: 'critical' },
};

function getVitals(id) {
  return VITALS[id] || { spo2: 97, pa: '125/78', fc: 72, glyc: 100, spo2Status: 'ok', paStatus: 'ok', fcStatus: 'ok', glycStatus: 'ok' };
}

const STATUS_COLORS = {
  ok: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-600 dark:text-emerald-400', ring: 'ring-emerald-200 dark:ring-emerald-800/40' },
  warning: { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-600 dark:text-amber-400', ring: 'ring-amber-200 dark:ring-amber-800/40' },
  critical: { bg: 'bg-red-50 dark:bg-red-950/30', text: 'text-red-600 dark:text-red-400', ring: 'ring-red-200 dark:ring-red-800/40' },
};

const TREND_DATA = [
  { day: 'Lun', spo2: 96, fc: 74, paMax: 130, paMin: 80 },
  { day: 'Mar', spo2: 95, fc: 78, paMax: 135, paMin: 82 },
  { day: 'Mer', spo2: 94, fc: 80, paMax: 140, paMin: 85 },
  { day: 'Gio', spo2: 93, fc: 82, paMax: 145, paMin: 90 },
  { day: 'Ven', spo2: 95, fc: 76, paMax: 138, paMin: 84 },
  { day: 'Sab', spo2: 96, fc: 74, paMax: 132, paMin: 80 },
  { day: 'Dom', spo2: 96, fc: 73, paMax: 130, paMin: 78 },
];

const SETTING_COLORS = {
  Bassa: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
  Media: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  Alta: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
};

/* ═══ MAIN COMPONENT ═══ */
export default function DiarioClinico() {
  const [selectedPatient, setSelectedPatient] = useState(PATIENTS[0]);
  const [activeTab, setActiveTab] = useState('Annotazioni');
  const [searchTerm, setSearchTerm] = useState('');
  const [noteText, setNoteText] = useState('');

  const filteredPatients = useMemo(() =>
    PATIENTS.filter(p => !searchTerm || p.name.toLowerCase().includes(searchTerm.toLowerCase())),
    [searchTerm]
  );

  const notes = getNotesFor(selectedPatient.id);
  const vitals = getVitals(selectedPatient.id);

  const ROLE_COLORS = {
    Infermiere: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-400',
    Fisioterapista: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400',
    'Medico UVMD': 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  };

  return (
    <AppLayout role="case-manager">
      <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="diario-page">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-4" data-testid="diario-title">Diario Clinico e Telemonitoraggio</h1>

        <div className="flex h-[700px] w-full bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="diario-container">

          {/* ══ LEFT: Patient List ══ */}
          <div className="w-80 border-r border-slate-200 dark:border-slate-800 flex flex-col shrink-0" data-testid="patient-sidebar">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">Pazienti Attivi</h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Cerca paziente..."
                  className="w-full h-9 pl-10 pr-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 transition-colors"
                  data-testid="patient-search"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto" data-testid="patient-list">
              {filteredPatients.map(p => (
                <div
                  key={p.id}
                  onClick={() => { setSelectedPatient(p); setActiveTab('Annotazioni'); }}
                  className={`px-4 py-3.5 border-b border-slate-100 dark:border-slate-800/60 cursor-pointer transition-all
                    ${selectedPatient.id === p.id
                      ? 'bg-cyan-50/60 dark:bg-slate-800 border-l-4 border-l-cyan-600'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/40 border-l-4 border-l-transparent'}`}
                  data-testid={`patient-item-${p.id}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{p.name}</p>
                    <Badge className={`${SETTING_COLORS[p.setting]} border-0 text-[9px] font-bold px-2 py-0.5`}>{p.setting}</Badge>
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 truncate">{p.diagnosis}</p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <Clock className="w-3 h-3 text-slate-400" />
                    <span className="text-[10px] text-slate-400 dark:text-slate-500">Ultimo agg. {p.lastUpdate}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ══ RIGHT: Detail Area ══ */}
          <div className="flex-1 flex flex-col bg-slate-50 dark:bg-[#0f172a] min-w-0" data-testid="detail-area">
            {/* Header + Tabs */}
            <div className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-6 pt-5 pb-0 shrink-0" data-testid="detail-header">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 dark:text-white">{selectedPatient.name} <span className="text-sm font-normal text-slate-500 dark:text-slate-400">- {selectedPatient.age} anni</span></h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{selectedPatient.diagnosis}</p>
                </div>
                <Badge className={`${SETTING_COLORS[selectedPatient.setting]} border-0 text-xs font-semibold px-3 py-1`}>{selectedPatient.setting} Complessita</Badge>
              </div>
              <div className="flex gap-0" data-testid="detail-tabs">
                {['Annotazioni', 'Parametri'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors
                      ${activeTab === tab
                        ? 'border-cyan-600 text-cyan-700 dark:text-cyan-400'
                        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'}`}
                    data-testid={`tab-${tab.toLowerCase()}`}
                  >
                    {tab === 'Annotazioni' ? 'Annotazioni Cliniche' : 'Telemonitoraggio Parametri'}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'Annotazioni' ? (
              /* ── TAB 1: Annotazioni ── */
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex-1 overflow-y-auto p-6" data-testid="notes-area">
                  <div className="flex flex-col gap-5">
                    {notes.map(note => (
                      <div key={note.id} className="bg-white dark:bg-slate-800 p-5 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700" data-testid={`note-${note.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Clock className="w-3.5 h-3.5 text-slate-400" />
                              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{note.date} - {note.time}</span>
                            </div>
                            <span className="text-slate-300 dark:text-slate-600">|</span>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                                <User className="w-3 h-3 text-slate-500 dark:text-slate-400" />
                              </div>
                              <span className="text-xs font-medium text-slate-700 dark:text-slate-200">{note.author}</span>
                            </div>
                          </div>
                          <Badge className={`${ROLE_COLORS[note.role] || 'bg-slate-100 text-slate-600'} border-0 text-[9px] font-semibold px-2 py-0.5`}>{note.role}</Badge>
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{note.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Input Area */}
                <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 shrink-0" data-testid="note-input-area">
                  <div className="flex gap-3">
                    <textarea
                      value={noteText}
                      onChange={e => setNoteText(e.target.value)}
                      placeholder="Aggiungi nuova annotazione clinica..."
                      rows={2}
                      className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 transition-colors resize-none"
                      data-testid="note-textarea"
                    />
                    <button
                      disabled={!noteText.trim()}
                      className="px-5 h-auto rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium flex items-center gap-2 shrink-0 transition-colors disabled:opacity-40"
                      data-testid="note-save-btn"
                    >
                      <Save className="w-4 h-4" /> Salva
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* ── TAB 2: Telemonitoraggio ── */
              <div className="flex-1 overflow-y-auto p-6 space-y-6" data-testid="vitals-area">
                {/* Vitals Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="vitals-grid">
                  {[
                    { label: 'SpO2', value: `${vitals.spo2}%`, icon: Droplets, status: vitals.spo2Status, unit: '' },
                    { label: 'Pressione', value: vitals.pa, icon: Activity, status: vitals.paStatus, unit: 'mmHg' },
                    { label: 'Freq. Cardiaca', value: `${vitals.fc}`, icon: Heart, status: vitals.fcStatus, unit: 'bpm' },
                    { label: 'Glicemia', value: `${vitals.glyc}`, icon: Thermometer, status: vitals.glycStatus, unit: 'mg/dL' },
                  ].map(v => {
                    const sc = STATUS_COLORS[v.status];
                    return (
                      <div key={v.label} className={`${sc.bg} rounded-xl p-4 ring-1 ${sc.ring}`} data-testid={`vital-${v.label.toLowerCase().replace(/[^a-z]/g, '')}`}>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{v.label}</span>
                          <v.icon className={`w-4 h-4 ${sc.text}`} />
                        </div>
                        <p className={`text-2xl font-bold ${sc.text}`}>{v.value}</p>
                        {v.unit && <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{v.unit}</p>}
                      </div>
                    );
                  })}
                </div>

                {/* Trend Chart */}
                <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm" data-testid="trend-chart">
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-4">Andamento Parametri - Ultimi 7 giorni</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={TREND_DATA} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #e2e8f0)" strokeOpacity={0.5} />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'var(--color-card, #fff)', border: '1px solid var(--color-border, #e2e8f0)', borderRadius: 12, fontSize: 12 }}
                      />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="spo2" name="SpO2 (%)" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="fc" name="FC (bpm)" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="paMax" name="PA Max" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="paMin" name="PA Min" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Alert History */}
                <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm" data-testid="alert-history">
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">Alert Recenti</h3>
                  <div className="flex flex-col gap-4">
                    {[
                      { date: '13/04 11:02', text: 'SpO2 scesa sotto soglia (88%). Verificata infermiere, rientrata.', level: 'critical' },
                      { date: '11/04 08:30', text: 'PA sistolica > 140 mmHg per 2 rilevazioni consecutive.', level: 'warning' },
                      { date: '08/04 14:15', text: 'FC > 100 bpm a riposo. Segnalato al medico curante.', level: 'warning' },
                    ].map((alert, i) => {
                      const sc = STATUS_COLORS[alert.level];
                      return (
                        <div key={i} className={`flex items-start gap-3 ${sc.bg} rounded-lg p-3 ring-1 ${sc.ring}`} data-testid={`alert-${i}`}>
                          <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${alert.level === 'critical' ? 'bg-red-500' : 'bg-amber-500'}`} />
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-700 dark:text-slate-200">{alert.text}</p>
                            <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{alert.date}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
